import actionTypes from 'constants/actionTypes'
import { createId } from 'lib/id'
import * as selectNodes from 'selectors/graph/nodes'
import { getNodeText } from 'lib/node'
import selectPermissions from 'selectors/permissions'
import { selectMe } from 'selectors/auth'
import type { RootState } from 'types/state'
import type { GraphNodeMap } from 'types/domain'
import type { ActionShape, DispatchFn, GetStateFn } from 'types/adapters'

type Dispatch = DispatchFn<ActionShape>
type GetState = GetStateFn

type Position = {
  x: number
  y: number
}

const selectAllNodesWithDeleted = selectNodes.all as unknown as (
  state: RootState,
  props: { params: { deleted: boolean } }
) => GraphNodeMap


export const selectOneNode = ({ id }: { id: string }) => {
  return {
    type: actionTypes.NODES.ONE.SELECT,
    payload: { id }
  }
}

export const deselectOneNode = ({ id }: { id: string }) => {
  return {
    type: actionTypes.NODES.ONE.DESELECT,
    payload: { id }
  }
}

export const createOneNode = (
  { content, kind, id, position }: { content?: string; kind?: string; id?: string; position?: Position },
  notify = false
) => {
  return (dispatch: Dispatch, getState: GetState) => {
    const state = getState() as RootState
    const { readers, writers } = selectPermissions(state)
    const { me } = selectMe(state)

    return dispatch({
      type: actionTypes.NODES.ONE.CREATE,
      payload: {
        id: id || createId(),
        content,
        kind,
        position,
        creator: me?.id,
        readers: Object.keys(readers),
        writers: Object.keys(writers)
      },
      meta: {
        retry: true,
        request: 'nodes.create',
        notify
      }
    })
  }
}

export const editOneNode = ({ id, content, kind }: { id: string; content?: string; kind?: string }, notify = false) => {
  return {
    type: actionTypes.NODES.ONE.EDIT,
    payload: {
      id,
      content,
      kind
    },
    meta: {
      retry: true,
      request: 'nodes.edit',
      notify
    }
  }
}

export const deleteOneNode = ({ id, autoConfirm = false }: { id: string; autoConfirm?: boolean }, notify = false) => {
  return {
    type: actionTypes.NODES.ONE.DELETE,
    payload: { id },
    meta: {
      retry: true,
      request: 'nodes.delete',
      notify,
      confirm: (state: RootState) => {
        if (notify || autoConfirm) {
          return null
        }
        const ns = selectAllNodesWithDeleted(state, {
          params: {
            deleted: true
          }
        })
        const nodeContent = getNodeText(ns[id])
        return `Are you sure you want to delete this node: ${nodeContent}`
      }
    }
  }
}

export const pinOneNode = ({ id }: { id: string }) => {
  return {
    type: actionTypes.NODES.ONE.PIN,
    payload: { id }
  }
}

export const unpinOneNode = ({ id }: { id: string }) => {
  return {
    type: actionTypes.NODES.ONE.UNPIN,
    payload: { id }
  }
}

export const selectMultiNode = ({ ids }: { ids: string[] }) => {
  return {
    type: actionTypes.NODES.MULTI.SELECT,
    payload: { ids }
  }
}

export const deleteMultiNode = ({ ids }: { ids: string[] }, notify = false) => {
  return (dispatch: Dispatch) => {
    if (ids.length === 0) {
      return
    }

    return dispatch({
      type: actionTypes.NODES.MULTI.DELETE,
      payload: { ids },
      meta: {
        retry: true,
        request: 'nodes.deleteMulti',
        notify,
        confirm: (state: RootState) => {
          if (notify) {
            return null
          }
          const ns = selectAllNodesWithDeleted(state, {
            params: {
              deleted: true
            }
          })
          const nodesContent = ids
            .map((id) => getNodeText(ns[id]))
            .join(', ')
          return `Are you sure you want to delete these nodes: ${nodesContent}`
        }
      }
    })
  }
}

export const pinMultiNode = ({ ids }: { ids: string[] }) => {
  return {
    type: actionTypes.NODES.MULTI.PIN,
    payload: { ids }
  }
}

export const unpinMultiNode = ({ ids }: { ids: string[] }) => {
  return {
    type: actionTypes.NODES.MULTI.UNPIN,
    payload: { ids }
  }
}
